import React, { useState, useEffect, useMemo } from 'react'
import { AlertCircle, Wand2, CheckCircle2, RefreshCw } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
  Button,
  Input,
  Select,
  SelectOption,
  Label,
  Checkbox,
  Badge,
} from '../ui'
import {
  usePreviewMonthlyLimits,
  useGenerateMonthlyLimits,
  type PositionLimitPreview,
} from '../../hooks/useStaffingLimits'
import { useOrganizationsForSelect } from '../../hooks/organizations/useOrganizations'
import { LoadingSpinner } from '../ui/loading-states'

const MONTH_NAMES = [
  'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
  'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'
]

interface GenerateMonthlyLimitsDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function GenerateMonthlyLimitsDialog({
  open,
  onOpenChange,
}: GenerateMonthlyLimitsDialogProps) {
  const now = new Date()
  const currentYear = now.getFullYear()
  const currentMonth = now.getMonth() + 1

  // Form state
  const [organizationId, setOrganizationId] = useState('')
  const [year, setYear] = useState(currentYear)
  const [month, setMonth] = useState(currentMonth)
  const [showPreview, setShowPreview] = useState(false)

  // Selection and editing state
  const [selectedPositions, setSelectedPositions] = useState<Set<string>>(new Set())
  const [editedHours, setEditedHours] = useState<Record<string, number>>({})

  // Data hooks
  const { data: organizationsData } = useOrganizationsForSelect()

  const {
    data: previewData,
    isLoading: isLoadingPreview,
    refetch: refetchPreview,
    error: previewError,
  } = usePreviewMonthlyLimits(
    { organization_id: organizationId, year, month },
    { enabled: showPreview && !!organizationId }
  )

  const generateLimits = useGenerateMonthlyLimits()

  // Generate year options
  const yearOptions = useMemo(() => {
    const years = []
    for (let y = currentYear + 1; y >= currentYear - 5; y--) {
      years.push(y)
    }
    return years
  }, [currentYear])

  // Initialize selection when preview data loads
  useEffect(() => {
    if (previewData?.positions) {
      const allPositionIds = new Set(previewData.positions.map(p => p.position_id))
      setSelectedPositions(allPositionIds)

      const initialHours: Record<string, number> = {}
      previewData.positions.forEach(p => {
        initialHours[p.position_id] = p.suggested_max_hours
      })
      setEditedHours(initialHours)
    }
  }, [previewData])

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (open) {
      setOrganizationId(organizationsData?.[0]?.id || '')
      setYear(currentYear)
      setMonth(currentMonth)
      setShowPreview(false)
      setSelectedPositions(new Set())
      setEditedHours({})
      generateLimits.reset()
    }
  }, [open, organizationsData, currentYear, currentMonth])

  const handlePreview = () => {
    if (organizationId) {
      setShowPreview(true)
    }
  }

  const handleResetPreview = () => {
    setShowPreview(false)
    setSelectedPositions(new Set())
    setEditedHours({})
  }

  const handleSelectAll = (checked: boolean) => {
    if (previewData?.positions) {
      if (checked) {
        setSelectedPositions(new Set(previewData.positions.map(p => p.position_id)))
      } else {
        setSelectedPositions(new Set())
      }
    }
  }

  const handleSelectPosition = (positionId: string, checked: boolean) => {
    setSelectedPositions(prev => {
      const newSet = new Set(prev)
      if (checked) {
        newSet.add(positionId)
      } else {
        newSet.delete(positionId)
      }
      return newSet
    })
  }

  const handleHoursChange = (positionId: string, value: string) => {
    const hours = parseInt(value, 10)
    if (!isNaN(hours) && hours > 0) {
      setEditedHours(prev => ({ ...prev, [positionId]: hours }))
    }
  }

  const handleGenerate = async () => {
    if (!previewData || selectedPositions.size === 0) return

    const limits = previewData.positions
      .filter(p => selectedPositions.has(p.position_id))
      .map(p => ({
        position_id: p.position_id,
        max_hours: editedHours[p.position_id] || p.suggested_max_hours,
      }))

    try {
      await generateLimits.mutateAsync({
        organization_id: organizationId,
        year,
        month,
        limits,
      })
      onOpenChange(false)
    } catch (error) {
      console.error('Generate limits error:', error)
    }
  }

  const handleClose = () => {
    if (!generateLimits.isPending) {
      onOpenChange(false)
    }
  }

  // Computed values for summary
  const selectedCount = selectedPositions.size
  const toCreateCount = previewData?.positions.filter(
    p => selectedPositions.has(p.position_id) && !p.existing_limit_id
  ).length || 0
  const toUpdateCount = previewData?.positions.filter(
    p => selectedPositions.has(p.position_id) && p.existing_limit_id
  ).length || 0
  const totalSelectedHours = previewData?.positions
    .filter(p => selectedPositions.has(p.position_id))
    .reduce((sum, p) => sum + (editedHours[p.position_id] || p.suggested_max_hours), 0) || 0

  const isAllSelected = previewData?.positions &&
    previewData.positions.length > 0 &&
    selectedPositions.size === previewData.positions.length

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wand2 className="h-5 w-5" />
            Автоматическое создание месячных лимитов
          </DialogTitle>
          <DialogDescription>
            Расчёт лимитов часов на основе графиков сотрудников
          </DialogDescription>
          {!generateLimits.isPending && <DialogClose onClose={handleClose} />}
        </DialogHeader>

        <div className="flex-1 overflow-auto">
          {/* Step 1: Select parameters */}
          {!showPreview && (
            <div className="space-y-4 p-1">
              <div>
                <Label htmlFor="org-select">Организация</Label>
                <Select
                  id="org-select"
                  value={organizationId}
                  onChange={e => setOrganizationId(e.target.value)}
                >
                  <SelectOption value="">Выберите организацию</SelectOption>
                  {organizationsData?.map((org: { id: string; name: string }) => (
                    <SelectOption key={org.id} value={org.id}>
                      {org.name}
                    </SelectOption>
                  ))}
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="year-select">Год</Label>
                  <Select
                    id="year-select"
                    value={year.toString()}
                    onChange={e => setYear(parseInt(e.target.value, 10))}
                  >
                    {yearOptions.map(y => (
                      <SelectOption key={y} value={y.toString()}>
                        {y}
                      </SelectOption>
                    ))}
                  </Select>
                </div>
                <div>
                  <Label htmlFor="month-select">Месяц</Label>
                  <Select
                    id="month-select"
                    value={month.toString()}
                    onChange={e => setMonth(parseInt(e.target.value, 10))}
                  >
                    {MONTH_NAMES.map((name, idx) => (
                      <SelectOption key={idx + 1} value={(idx + 1).toString()}>
                        {name}
                      </SelectOption>
                    ))}
                  </Select>
                </div>
              </div>

              <Button
                onClick={handlePreview}
                disabled={!organizationId}
                className="w-full"
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                Рассчитать
              </Button>
            </div>
          )}

          {/* Step 2: Preview and edit */}
          {showPreview && (
            <div className="space-y-4 p-1">
              {/* Header with selected params */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Badge variant="secondary">
                    {previewData?.organization_name || '...'}
                  </Badge>
                  <Badge variant="outline">
                    {MONTH_NAMES[month - 1]} {year}
                  </Badge>
                </div>
                <Button variant="outline" size="sm" onClick={handleResetPreview}>
                  Изменить параметры
                </Button>
              </div>

              {/* Loading state */}
              {isLoadingPreview && (
                <div className="flex items-center justify-center py-8">
                  <LoadingSpinner className="mr-2 h-5 w-5" />
                  <span className="text-muted-foreground">Расчёт часов...</span>
                </div>
              )}

              {/* Error state */}
              {previewError && (
                <div className="flex items-center p-3 text-sm text-red-700 bg-red-100 rounded-md">
                  <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" />
                  <span>Ошибка при расчёте: {(previewError as Error).message}</span>
                </div>
              )}

              {/* Preview table */}
              {previewData && previewData.positions.length > 0 && (
                <>
                  <div className="border rounded-md overflow-hidden">
                    <div className="max-h-[350px] overflow-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-muted sticky top-0">
                          <tr>
                            <th className="w-10 p-3 text-left">
                              <Checkbox
                                checked={isAllSelected}
                                onCheckedChange={(checked) => handleSelectAll(checked === true)}
                              />
                            </th>
                            <th className="p-3 text-left font-medium">Должность</th>
                            <th className="p-3 text-center font-medium w-24">Сотр.</th>
                            <th className="p-3 text-center font-medium w-28">Расчёт</th>
                            <th className="p-3 text-center font-medium w-28">Текущий</th>
                            <th className="p-3 text-center font-medium w-32">Новое значение</th>
                            <th className="p-3 text-center font-medium w-24">Статус</th>
                          </tr>
                        </thead>
                        <tbody>
                          {previewData.positions.map((position: PositionLimitPreview) => (
                            <tr
                              key={position.position_id}
                              className={`border-t hover:bg-muted/50 ${
                                !selectedPositions.has(position.position_id)
                                  ? 'opacity-50'
                                  : ''
                              }`}
                            >
                              <td className="p-3">
                                <Checkbox
                                  checked={selectedPositions.has(position.position_id)}
                                  onCheckedChange={(checked) =>
                                    handleSelectPosition(
                                      position.position_id,
                                      checked === true
                                    )
                                  }
                                />
                              </td>
                              <td className="p-3">{position.position_name}</td>
                              <td className="p-3 text-center">
                                <Badge variant="outline">{position.employee_count}</Badge>
                              </td>
                              <td className="p-3 text-center">
                                {position.calculated_hours} ч.
                              </td>
                              <td className="p-3 text-center">
                                {position.existing_max_hours ? (
                                  <span className="text-muted-foreground">
                                    {position.existing_max_hours} ч.
                                  </span>
                                ) : (
                                  <span className="text-muted-foreground">—</span>
                                )}
                              </td>
                              <td className="p-3">
                                <Input
                                  type="number"
                                  min={1}
                                  value={editedHours[position.position_id] || position.suggested_max_hours}
                                  onChange={e =>
                                    handleHoursChange(position.position_id, e.target.value)
                                  }
                                  className="w-full h-8 text-center"
                                  disabled={!selectedPositions.has(position.position_id)}
                                />
                              </td>
                              <td className="p-3 text-center">
                                {position.existing_limit_id ? (
                                  <Badge variant="warning" className="text-xs">
                                    Обновить
                                  </Badge>
                                ) : (
                                  <Badge variant="success" className="text-xs">
                                    Создать
                                  </Badge>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Summary */}
                  <div className="flex items-center justify-between p-3 bg-muted rounded-md">
                    <div className="flex items-center gap-4 text-sm">
                      <span>
                        Выбрано: <strong>{selectedCount}</strong>
                      </span>
                      <span className="text-green-600">
                        Создать: <strong>{toCreateCount}</strong>
                      </span>
                      <span className="text-amber-600">
                        Обновить: <strong>{toUpdateCount}</strong>
                      </span>
                    </div>
                    <div className="text-sm">
                      Итого часов: <strong>{totalSelectedHours.toLocaleString()}</strong>
                    </div>
                  </div>
                </>
              )}

              {/* Empty state */}
              {previewData && previewData.positions.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  <AlertCircle className="mx-auto h-8 w-8 mb-2" />
                  <p>Не найдено сотрудников с назначенными графиками</p>
                  <p className="text-sm mt-1">
                    Убедитесь, что сотрудникам назначены шаблоны графиков
                  </p>
                </div>
              )}

              {/* Success message */}
              {generateLimits.isSuccess && (
                <div className="flex items-center p-3 text-sm text-green-700 bg-green-100 rounded-md">
                  <CheckCircle2 className="h-4 w-4 mr-2 flex-shrink-0" />
                  <span>
                    Создано: {generateLimits.data.created}, обновлено:{' '}
                    {generateLimits.data.updated}
                  </span>
                </div>
              )}

              {/* Error message */}
              {generateLimits.error && (
                <div className="flex items-center p-3 text-sm text-red-700 bg-red-100 rounded-md">
                  <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" />
                  <span>
                    Ошибка при создании лимитов:{' '}
                    {(generateLimits.error as Error).message}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={generateLimits.isPending}
          >
            Отмена
          </Button>
          {showPreview && previewData && previewData.positions.length > 0 && (
            <Button
              onClick={handleGenerate}
              disabled={generateLimits.isPending || selectedCount === 0}
            >
              {generateLimits.isPending ? (
                <>
                  <LoadingSpinner className="mr-2 h-4 w-4" />
                  Создание...
                </>
              ) : (
                <>
                  <Wand2 className="mr-2 h-4 w-4" />
                  Создать лимиты ({selectedCount})
                </>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
